import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { HUT_ORIGIN, HUT_DOOR_OUTSIDE } from './hut.js';
import { FARM_ORIGIN } from './farmLayout.js';
import { DOCK_APPROACH } from './dockNavigation.js';
import { createFoliageMaterial } from './foliageMaterial.js';

// Shared palette follows visual-redesign-v1. All colour remains light driven,
// so the sunlit art direction still participates in nights and storm weather.
export function createIslandAtlasMaterial(map) {
  const material = new THREE.MeshStandardMaterial({ map, vertexColors: true, roughness: .9 });
  material.onBeforeCompile = shader => {
    shader.fragmentShader = shader.fragmentShader.replace('#include <map_fragment>', `#include <map_fragment>
      float artTile=floor(vMapUv.x*4.)+4.*(3.-floor(vMapUv.y*4.));
      vec3 artBase=diffuseColor.rgb;
      float artTexture=clamp(dot(artBase,vec3(.2126,.7152,.0722))*1.9,.72,1.12);
      if(artTile==2.) diffuseColor.rgb=mix(artBase,vec3(.39,.20,.075)*artTexture,.72);
      if(artTile==14.) diffuseColor.rgb=mix(artBase,vec3(.23,.105,.035)*artTexture,.52);
      if(artTile==3.) diffuseColor.rgb=mix(artBase,vec3(.90,.81,.64)*artTexture,.78);
      if(artTile==4.) diffuseColor.rgb=mix(artBase,vec3(.65,.235,.065)*artTexture,.7);
      if(artTile==7.) diffuseColor.rgb=mix(artBase,vec3(.38,.40,.34)*artTexture,.4);`);
  };
  material.customProgramCacheKey = () => 'sunlit-island-atlas-v1';
  return material;
}

export function createMeadowMaterial(map = null) {
  const material = new THREE.MeshStandardMaterial({ color: 0xffffff, map, roughness: 1 });
  material.onBeforeCompile = shader => {
    shader.vertexShader = shader.vertexShader.replace('#include <common>', '#include <common>\nvarying vec3 vMeadowWorld;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvMeadowWorld=(modelMatrix*vec4(transformed,1.)).xyz;');
    shader.fragmentShader = shader.fragmentShader.replace('#include <common>', `#include <common>
      varying vec3 vMeadowWorld;
      float meadowLine(vec2 p,vec2 a,vec2 b){vec2 d=b-a;return length(p-a-d*clamp(dot(p-a,d)/dot(d,d),0.,1.));}`)
      .replace('#include <map_fragment>', `
        vec2 mp=vMeadowWorld.xz;
        diffuseColor.rgb *= mix(texture2D(map, mp * .30).rgb, vec3(.32,.43,.07), .38);
        float meadowPatch=sin(mp.x*.9+sin(mp.y*.65))*sin(mp.y*.85-mp.x*.3);
        diffuseColor.rgb*=.94+meadowPatch*.08;
        float trail=min(meadowLine(mp,vec2(${HUT_DOOR_OUTSIDE.x.toFixed(2)},${(HUT_DOOR_OUTSIDE.z-.8).toFixed(2)}),vec2(${FARM_ORIGIN.x.toFixed(2)},${FARM_ORIGIN.z.toFixed(2)})),meadowLine(mp,vec2(${HUT_DOOR_OUTSIDE.x.toFixed(2)},${(HUT_DOOR_OUTSIDE.z-.8).toFixed(2)}),vec2(${DOCK_APPROACH.x.toFixed(2)},${DOCK_APPROACH.z.toFixed(2)})));
        trail=min(trail,meadowLine(mp,vec2(${HUT_DOOR_OUTSIDE.x.toFixed(2)},${(HUT_DOOR_OUTSIDE.z-.8).toFixed(2)}),vec2(${HUT_ORIGIN.x.toFixed(2)},${(HUT_ORIGIN.z-4).toFixed(2)})));
        float path=(1.-smoothstep(.44,.94,trail))*(.88+.08*sin(mp.x*12.+mp.y*5.));
        diffuseColor.rgb=mix(diffuseColor.rgb,vec3(.69,.57,.32),path*.8);`);
  };
  material.customProgramCacheKey = () => 'sunlit-meadow-texture-v3';
  return material;
}

export function createShallowFloorMaterial(surfUniforms) {
  const material=new THREE.MeshStandardMaterial({color:0xffffff,roughness:1});
  material.onBeforeCompile=shader=>{
    shader.uniforms.uFloorTime=surfUniforms.uTime;
    shader.uniforms.uFloorCaustics=surfUniforms.uCaustics;
    shader.vertexShader=shader.vertexShader.replace('#include <common>','#include <common>\nvarying vec3 vFloorWorld;')
      .replace('#include <begin_vertex>','#include <begin_vertex>\nvFloorWorld=(modelMatrix*vec4(transformed,1.)).xyz;');
    shader.fragmentShader=shader.fragmentShader.replace('#include <common>','#include <common>\nvarying vec3 vFloorWorld;uniform float uFloorTime;uniform sampler2D uFloorCaustics;')
      .replace('#include <color_fragment>',`#include <color_fragment>
        float shelfDepth=1.-smoothstep(-1.7,-.15,vFloorWorld.y);
        diffuseColor.rgb=mix(vec3(.62,.65,.44),vec3(.075,.35,.32),shelfDepth);
        vec2 fp=vFloorWorld.xz;
        float caustic=texture2D(uFloorCaustics,fp*.24+vec2(uFloorTime*.012,-uFloorTime*.008)).r;
        diffuseColor.rgb+=vec3(.19,.24,.18)*caustic*(1.-shelfDepth*.5);`);
  };
  material.customProgramCacheKey=()=> 'clear-lagoon-floor-v2';return material;
}

function frondsGeometry(fern = false) {
  const positions = [], indices = [], colors = [];
  for (let blade = 0; blade < (fern ? 7 : 5); blade++) {
    const angle = blade * 2.399, length = fern ? .45 + blade % 3 * .08 : .16 + blade % 3 * .04;
    const width = fern ? .12 : .037;
    const first = positions.length / 3;
    for (let station = 0; station <= 6; station++) {
      const t = station / 6, distance = length * t;
      const w = width * Math.sin(Math.PI * t) * (fern && station % 2 ? .68 : 1);
      for (const side of [-1, 1]) {
        positions.push(Math.cos(angle) * distance + Math.sin(angle) * w * side,
          fern ? .04 + Math.sin(t * Math.PI * .85) * .30 : length * t * (1 - t * .32),
          Math.sin(angle) * distance - Math.cos(angle) * w * side);
        const c = new THREE.Color(fern ? 0x698d38 : blade % 2 ? 0xb6c966 : 0x8ca847);
        c.multiplyScalar(.86 + t * .14); colors.push(c.r, c.g, c.b);
      }
      if (station < 6) { const a = first + station * 2; indices.push(a, a + 2, a + 1, a + 1, a + 2, a + 3); }
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices); geometry.computeVertexNormals(); return geometry;
}

export function addMeadowAccents(scene, { heightAt, trees, windUniforms, clearOfFarm }) {
  const hash = n => { const v = Math.sin(n * 127.1 + 31.7) * 43758.54; return v - Math.floor(v); };
  const sites = [];
  for (let i = 0; i < 1400; i++) {
    const angle = hash(i + 1) * Math.PI * 2, radius = 3 + hash(i + 321) * 7.8;
    const x = Math.cos(angle) * radius, z = Math.sin(angle) * radius;
    if (!clearOfFarm(x, z, .8) || (Math.abs(x - HUT_ORIGIN.x) < 2.65 && Math.abs(z - HUT_ORIGIN.z) < 3.6)) continue;
    const nearTree = trees.some(t => Math.hypot(t.x - x, t.z - z) < 1.6);
    if (!nearTree && hash(i + 79) < .32) continue;
    sites.push({ x, z, y: heightAt(x, z) + .012, size: .6 + hash(i + 11) * .7, angle });
  }
  const material = createFoliageMaterial(windUniforms);
  for (const fern of [false, true]) {
    const placements = fern ? sites.filter((_, i) => i % 7 === 0) : sites;
    const instances = new THREE.InstancedMesh(frondsGeometry(fern), material, placements.length);
    const dummy = new THREE.Object3D();
    placements.forEach((p, i) => { dummy.position.set(p.x, p.y, p.z); dummy.rotation.y = p.angle;
      dummy.scale.setScalar(p.size); dummy.updateMatrix(); instances.setMatrixAt(i, dummy.matrix); });
    instances.name = fern ? 'Meadow_Ferns' : 'Meadow_Grass';
    instances.receiveShadow = true; scene.add(instances);
  }
  const flowerParts = [];
  for (let petal = 0; petal < 5; petal++) {
    const a = petal * Math.PI * 2 / 5;
    const g = new THREE.SphereGeometry(.042, 5, 3);
    g.scale(1.1, .32, .7); g.translate(Math.cos(a)*.038,.26,Math.sin(a)*.038);
    flowerParts.push(g);
  }
  const flowers = sites.filter((_, i) => i % 5 === 0);
  const petals = new THREE.InstancedMesh(mergeGeometries(flowerParts),
    new THREE.MeshStandardMaterial({color:0xfff5cb,roughness:1}), flowers.length);
  const dummy = new THREE.Object3D();
  flowers.forEach((p, i) => {dummy.position.set(p.x,p.y,p.z);dummy.rotation.y=p.angle;
    dummy.scale.setScalar(p.size);dummy.updateMatrix();petals.setMatrixAt(i,dummy.matrix);});
  petals.name='Meadow_WhiteFlowers';petals.receiveShadow=true;scene.add(petals);
}

function roofTileGeometry() {
  const positions = [], indices = [], steps = 6;
  for (const x of [-.29, .29]) for (let i = 0; i <= steps; i++) {
    const angle = i / steps * Math.PI;
    positions.push(x, Math.sin(angle) * .095, -.185 + .37 * i / steps);
  }
  for (let i = 0; i < steps; i++) indices.push(i, i + 7, i + 1, i + 1, i + 7, i + 8);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices); geometry.computeVertexNormals(); return geometry;
}

export function addHutDetails(model) {
  const roof = new THREE.Group(); roof.name = 'HutRoof_TerracottaDetails';
  const tileMaterial = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: .92, side: THREE.DoubleSide });
  const tiles = new THREE.InstancedMesh(roofTileGeometry(), tileMaterial, 120);
  const dummy = new THREE.Object3D(), tileColors = [0xbb5a29, 0xca6d33, 0xad4b23, 0xd07939].map(c => new THREE.Color(c));
  let index = 0;
  for (const side of [-1, 1]) for (let row = 0; row < 5; row++) for (let col = 0; col < 12; col++) {
    const t = (row + .5) / 5;
    dummy.position.set(side * 2.42 * t, 3.67 - 1.24 * t + .055, -1.83 + col * .333);
    dummy.rotation.set(0, 0, -side * Math.atan2(1.24, 2.42)); dummy.updateMatrix();
    tiles.setMatrixAt(index, dummy.matrix); tiles.setColorAt(index, tileColors[(row * 7 + col) % 4]); index++;
  }
  tiles.castShadow = tiles.receiveShadow = true; roof.add(tiles); model.add(roof);
  const wood = new THREE.MeshStandardMaterial({ color: 0x9a6a36, roughness: .9 });
  const stone = new THREE.MeshStandardMaterial({ color: 0xb4ae94, roughness: 1 });
  const geometry = new RoundedBoxGeometry(1, 1, 1, 1, .05);
  const batches = new Map(), batchBounds = new Map();
  const beam = (x, y, z, sx, sy, sz, material = wood) => {
    const m = new THREE.Mesh(geometry, material); m.position.set(x, y, z); m.scale.set(sx, sy, sz);
    m.updateMatrix();const g=geometry.clone().applyMatrix4(m.matrix);
    g.computeBoundingBox();
    const bounds=g.boundingBox;
    if(!batchBounds.has(material))batchBounds.set(material,[]);
    batchBounds.get(material).push({x0:bounds.min.x,x1:bounds.max.x,z0:bounds.min.z,z1:bounds.max.z,bottom:bounds.min.y,top:bounds.max.y});
    if(!batches.has(material))batches.set(material,[]);batches.get(material).push(g);return m;
  };
  // Rails leave the centre entrance and the existing crossing lane unobstructed.
  for (const side of [-1, 1]) {
    beam(side * 1.53, .76, -2.75, .09, .12, 1.15);
    beam(side * 1.53, .36, -2.75, .07, .09, 1.15);
    for (const z of [-2.23, -3.27]) beam(side * 1.53, .5, z, .14, .85, .14);
    beam(side * 1.18, .76, -3.34, .68, .12, .1);
  }
  for (let row = 0; row < 2; row++) for (let col = 0; col < 9; col++) {
    beam(-1.78 + col * .445 + (row % 2 ? .08 : 0), .13 + row * .17, -1.52, .39, .145, .11, stone);
  }
  // Filled gables close the gap under the roof; all roof details follow cutaway.
  const plaster = new THREE.MeshStandardMaterial({color:0xe9d6ae,roughness:1,side:THREE.DoubleSide});
  const gableGeometry = new THREE.BufferGeometry();
  gableGeometry.setAttribute('position',new THREE.Float32BufferAttribute([
    -1.86,2.43,-1.54, 1.86,2.43,-1.54, 0,3.66,-1.54,
    1.86,2.43,1.54, -1.86,2.43,1.54, 0,3.66,1.54],3));
  gableGeometry.computeVertexNormals();
  const gables=new THREE.Mesh(gableGeometry,plaster);gables.castShadow=gables.receiveShadow=true;roof.add(gables);
  const roofWood=[];
  function slope(a,b,width=.12) {
    const start=new THREE.Vector3(...a),end=new THREE.Vector3(...b),mid=start.clone().add(end).multiplyScalar(.5);
    const obj=new THREE.Object3D();obj.position.copy(mid);obj.quaternion.setFromUnitVectors(new THREE.Vector3(0,1,0),end.sub(start).normalize());
    obj.scale.set(width,new THREE.Vector3(...a).distanceTo(new THREE.Vector3(...b)),width);obj.updateMatrix();
    roofWood.push(geometry.clone().applyMatrix4(obj.matrix));
  }
  for(const z of [-1.60,1.60]) {
    slope([-1.93,2.42,z],[0,3.73,z],.15);slope([0,3.73,z],[1.93,2.42,z],.15);
    slope([0,2.44,z],[0,3.55,z],.12);slope([-1.72,2.50,z],[1.72,2.50,z],.13);
  }
  const gableBeams=new THREE.Mesh(mergeGeometries(roofWood),wood);gableBeams.castShadow=true;roof.add(gableBeams);
  const chimney=new THREE.Mesh(new RoundedBoxGeometry(.68,1.40,.68,2,.07),stone);
  chimney.position.set(-.95,3.98,.85);chimney.castShadow=chimney.receiveShadow=true;roof.add(chimney);
  const cap=new THREE.Mesh(new RoundedBoxGeometry(.88,.14,.88,1,.04),stone);cap.position.set(-.95,4.68,.85);cap.castShadow=true;roof.add(cap);
  // Side shutter frames, a shaded lantern, and barrels enrich the lived-in porch.
  for(const z of [-.14,.94])beam(1.94,1.5,z,.10,1.0,.09);
  for(const y of [1.05,1.94])beam(1.94,y,.4,.10,.10,1.16);
  beam(1.97,1.5,.4,.08,.86,.08);
  const metal=new THREE.MeshStandardMaterial({color:0x3e4033,roughness:.8});
  const glass=new THREE.MeshStandardMaterial({color:0xffdd8b,emissive:0xd88c2d,emissiveIntensity:.35,roughness:.5});
  beam(-.78,1.42,-1.78,.21,.28,.15,glass);
  for(const x of [-.90,-.66])beam(x,1.42,-1.88,.025,.36,.025,metal);
  for(const y of [1.24,1.60])beam(-.78,y,-1.81,.29,.04,.23,metal);
  beam(-.78,1.78,-1.77,.04,.22,.04,metal);
  for(const [x,z] of [[-2.14,-1.98],[-2.25,-2.65]]) {
    const barrel=new THREE.Mesh(new THREE.CylinderGeometry(.23,.22,.58,10),wood);
    barrel.geometry.computeBoundingBox();
    const bounds=barrel.geometry.boundingBox;
    barrel.userData.physicalParts=[{x0:bounds.min.x,x1:bounds.max.x,z0:bounds.min.z,z1:bounds.max.z,bottom:bounds.min.y,top:bounds.max.y}];
    barrel.position.set(x,.35,z);barrel.castShadow=barrel.receiveShadow=true;model.add(barrel);
    for(const y of [.15,.52]) {const ring=new THREE.Mesh(new THREE.TorusGeometry(.235,.018,4,10),metal);
      ring.rotation.x=Math.PI/2;ring.position.set(x,y,z);model.add(ring);}
  }
  for(const [material,parts] of batches) {
    const batch=new THREE.Mesh(mergeGeometries(parts),material);batch.name='Hut_DetailBatch';
    batch.userData.physicalParts=batchBounds.get(material);
    batch.castShadow=batch.receiveShadow=true;model.add(batch);
    for(const part of parts)part.dispose();
  }
  return roof;
}

export function createFarmEnclosure() {
  const geometry=new RoundedBoxGeometry(1,1,1,1,.035),parts=[],physicalParts=[];
  const add=(x,y,z,sx,sy,sz)=>{const g=geometry.clone();g.scale(sx,sy,sz);g.translate(x,y,z);g.computeBoundingBox();
    const bounds=g.boundingBox;
    physicalParts.push({id:`member-${parts.length}`,x0:bounds.min.x,x1:bounds.max.x,z0:bounds.min.z,z1:bounds.max.z,bottom:bounds.min.y,top:bounds.max.y});parts.push(g);};
  for(const x of [-2.52,0,2.52])for(const z of [-2.20,2.20]) add(x,.43,z,.15,.86,.15);
  for(const z of [-.82,.82])add(2.52,.43,z,.15,.86,.15);
  for(const y of [.35,.68]) {
    for(const z of [-2.20,2.20])add(0,y,z,5.04,.075,.075);
    add(-2.52,y,0,.075,.075,4.4);
    for(const z of [-1.51,1.51])add(2.52,y,z,.075,.075,1.38);
  }
  const mesh=new THREE.Mesh(mergeGeometries(parts),new THREE.MeshStandardMaterial({color:0xa8793e,roughness:1}));
  mesh.userData.physicalParts=physicalParts;
  mesh.name='Farm_WoodEnclosure';mesh.castShadow=mesh.receiveShadow=true;geometry.dispose();
  for(const part of parts)part.dispose();return mesh;
}
